import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';

import { DeviceContext } from '@vyce/core/src/contexts';
import { buildFormUrlSegment, getPaddingForContent } from '@vyce/core/src/utils';
import { ConfirmDialog } from '@vyce/core/src/components';
import { FormDoc, FormRecipientsDialogType, User } from '@vyce/core/src/types';
import { FormFolderDoc } from '@vyce/core/src/api/types';
import { SafeDndProvider } from '@vyce/core/src/components/SafeDndProvider';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { getFormsSortParams, useFormList } from './hooks/useFormList';
import { useCreateFormData } from './hooks/useCreateFormData';
import { useFormRecipients } from './hooks/useFormRecipients';
import { FormSendDialog } from './components/FormSendDialog';
import { FormAIAnalyticsDialog } from './components/FormAIAnalyticsDialog';
import {
  addFormToFolderRequest,
  archiveFormFolderRequest,
  createFormFolderRequest,
  deleteFormFolderRequest,
  getFormFoldersRequest,
  getFormsRequest,
  moveFormFolderRequest,
  sendFormMessageRequest,
  updateFormFolderRequest,
  updateFormStatusRequest,
} from '@vyce/core/src/api/forms';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { FormRecipientsDialog } from './components/FormRecipientsDialog';
import { CreateFolderDialog } from './components/CreateFolderDialog';
import { ChangeFolderDialog } from './components/ChangeFolderDialog';
import { MoveFolderDialog } from './components/MoveFolderDialog';
import { RenameFolderDialog } from './components/RenameFolderDialog';
import { FolderCard } from './components/FolderCard';
import { FolderCardSkeleton } from './components/FolderCardSkeleton';
import { MyFormsToolbar } from './components/MyFormsToolbar';
import { FormsDataGrid } from './components/FormsDataGrid';
import { UnassignedFormsSection } from './components/UnassignedFormsSection';
import { GRID_PAGE_SIZE } from '../../constants';
import { useDebounceValue } from '@vyce/core/src/hooks/useDebounceValue';

const FORM_DRAG_TYPE = 'FORM_UNASSIGNED_CARD';

/** One getForms batch for folder view; split client-side by folderId. */
const FOLDER_VIEW_FORMS_FETCH_LIMIT = 500;
const FORM_FOLDERS_FETCH_LIMIT = 500;

export const MyForms: React.FC<{ user: User; companyId: string }> = ({ user, companyId }) => {
  const { isMobile } = useContext(DeviceContext);
  const padding = getPaddingForContent(isMobile);
  const history = useHistory();
  const location = useLocation();
  const [openDeleteFormDialog, setOpenDeleteFormDialog] = useState<boolean>(false);
  const [openArchiveFormDialog, setOpenArchiveFormDialog] = useState<boolean>(false);
  const [loadingArchiveForm, setLoadingArchiveForm] = useState<boolean>(false);
  const [openSendFormDialog, setOpenSendFormDialog] = useState<boolean>(false);
  const [openChangeFolderDialog, setOpenChangeFolderDialog] = useState<boolean>(false);
  const [selectedFormId, setSelectedFormId] = useState<string | undefined>();
  const [loadingChangeFolder, setLoadingChangeFolder] = useState<boolean>(false);
  const [openAIAnalyticsDialog, setOpenAIAnalyticsDialog] = useState<boolean>(false);
  const [selectedFormForAIAnalytics, setSelectedFormForAIAnalytics] = useState<FormDoc | null>(null);
  const [openResendAllDialog, setOpenResendAllDialog] = useState<boolean>(false);
  const [loadingResendAll, setLoadingResendAll] = useState<boolean>(false);
  const [isArchivedView, setIsArchivedView] = useState<boolean>(false);
  const [folderFilter, setFolderFilter] = useState<string>('');
  const [folderSearch, setFolderSearch] = useState<string>('');
  const debouncedFolderSearch = useDebounceValue(folderSearch, 300);
  const folderSearchForApi = debouncedFolderSearch === null ? '' : debouncedFolderSearch.trim();
  const [folders, setFolders] = useState<FormFolderDoc[]>([]);
  const [folderFormsMap, setFolderFormsMap] = useState<Record<string, FormDoc[]>>({});
  const [unassignedForms, setUnassignedForms] = useState<FormDoc[]>([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [loadingFolderView, setLoadingFolderView] = useState<boolean>(false);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState<boolean>(false);
  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [openDeleteFolderDialog, setOpenDeleteFolderDialog] = useState<boolean>(false);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedFolder, setSelectedFolder] = useState<FormFolderDoc | null>(null);
  const [deletingFolder, setDeletingFolder] = useState<boolean>(false);
  const [openMoveFolderDialog, setOpenMoveFolderDialog] = useState<boolean>(false);
  const [movingFolder, setMovingFolder] = useState<boolean>(false);
  const [openRenameFolderDialog, setOpenRenameFolderDialog] = useState<boolean>(false);
  const [renamingFolder, setRenamingFolder] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [dialogType, setDialogType] = useState<FormRecipientsDialogType>('recipients');
  const [openRecipientsDialog, setOpenRecipientsDialog] = useState<boolean>(false);
  const { showNotification, handleServerError } = useContext(NotificationContext);
  const showNotificationRef = useRef(showNotification);
  const handleServerErrorRef = useRef(handleServerError);
  const isMountedRef = useRef(true);
  const { deleteForm, duplicateForm, loading: loadingDeleteForm } = useCreateFormData({ companyId, user });
  const [loadingSendForm, setLoadingSendForm] = useState<boolean>(false);
  const {
    isPhoneInputDisabled,
    handleEmailFieldKeyDown,
    handlePhoneFieldKeyDown,
    addEmailToList,
    addPhoneToList,
    deleteContact,
    emails,
    phones,
    setIsPhoneInputDisabled,
    bulkPaste,
    bulkLimitHint,
    recipientsFormMethods,
    sendFormRecipients,
  } = useFormRecipients({ user, companyId });

  const {
    forms,
    loading,
    getForms,
    total,
    handlePageChange,
    setOffset,
    handleSearchChange,
    sortModel,
    substring,
    offset,
    handleSortModelChange,
  } = useFormList({ user, companyId });
  const statusFilter = isArchivedView ? 'archived' : undefined;
  const isFolderView = new URLSearchParams(location.search).get('view') !== 'list';

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  useEffect(() => {
    handleServerErrorRef.current = handleServerError;
  }, [handleServerError]);

  const fetchUnassignedPage = useCallback(async () => {
    const { sortBy, sortOrder } = getFormsSortParams(sortModel);
    return getFormsRequest({
      offset,
      limit: GRID_PAGE_SIZE,
      substring,
      companyId,
      userId: user.uuid,
      sortBy,
      sortOrder,
      status: statusFilter,
      folderId: 'unassigned',
    });
  }, [sortModel, offset, substring, companyId, user.uuid, statusFilter]);

  const refreshUnassignedAfterMutation = useCallback(async () => {
    setLoadingUnassigned(true);
    try {
      const res = await fetchUnassignedPage();
      if (!isMountedRef.current) return;
      setUnassignedForms(res.forms);
      setUnassignedTotal(res.total);
    } catch (e) {
      if (isMountedRef.current) handleServerErrorRef.current(e);
    } finally {
      if (isMountedRef.current) setLoadingUnassigned(false);
    }
  }, [fetchUnassignedPage]);

  useEffect(() => {
    if (!companyId || !isFolderView) return;
    let cancelled = false;
    (async () => {
      setLoadingUnassigned(true);
      try {
        const res = await fetchUnassignedPage();
        if (!cancelled) {
          setUnassignedForms(res.forms);
          setUnassignedTotal(res.total);
        }
      } catch (e) {
        if (!cancelled) handleServerErrorRef.current(e);
      } finally {
        if (!cancelled) setLoadingUnassigned(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, isFolderView, fetchUnassignedPage]);

  const reloadFoldersBoard = useCallback(async () => {
    try {
      setLoadingFolderView(true);
      const { folders: fetchedFolders } = await getFormFoldersRequest({
        companyId,
        userId: user.uuid,
        search: folderSearchForApi || null,
        limit: FORM_FOLDERS_FETCH_LIMIT,
        archived: isArchivedView,
      });
      if (!isMountedRef.current) return;
      setFolders(fetchedFolders);

      const { forms: allForms } = await getFormsRequest({
        offset: 0,
        limit: FOLDER_VIEW_FORMS_FETCH_LIMIT,
        substring: '',
        companyId,
        userId: user.uuid,
        status: statusFilter,
      });

      if (!isMountedRef.current) return;
      const nextMap: Record<string, FormDoc[]> = {};
      fetchedFolders.forEach(folder => {
        nextMap[folder.id] = allForms.filter(f => f.folderId === folder.id);
      });
      setFolderFormsMap(nextMap);
    } catch (error) {
      console.error(error);
      if (isMountedRef.current) {
        showNotificationRef.current({ message: 'Failed to load folders', options: { variant: 'error' } });
      }
    } finally {
      if (isMountedRef.current) setLoadingFolderView(false);
    }
  }, [companyId, user.uuid, statusFilter, folderSearchForApi, isArchivedView]);

  const handleSendFormRecipients = async () => {
    if (!selectedFormId) return;
    setLoadingSendForm(true);
    await sendFormRecipients(selectedFormId);
    setOpenSendFormDialog(false);
    setLoadingSendForm(false);
  };

  const handleResendAll = async () => {
    if (!selectedFormId) return;
    try {
      setLoadingResendAll(true);
      await sendFormMessageRequest({
        companyId,
        formId: selectedFormId,
        userId: user.uuid,
        sendToAll: true,
      });
      showNotification({ message: 'Form has been sent', options: { variant: 'success' } });
      setSelectedFormId(undefined);
      setOpenResendAllDialog(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingResendAll(false);
    }
  };

  useEffect(() => {
    if (companyId && !isFolderView) {
      getForms(statusFilter, folderFilter || undefined);
    }
    // getForms is intentionally omitted to avoid unstable callback loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, isFolderView, statusFilter, sortModel, offset, substring, folderFilter]);

  useEffect(() => {
    if (!companyId || !isFolderView) return;
    void reloadFoldersBoard();
  }, [companyId, isFolderView, reloadFoldersBoard]);

  // List view only: folder view owns `folders` via reloadFoldersBoard (search/archived aware).
  // Running this in folder view would duplicate getFormFolders and race setFolders.
  useEffect(() => {
    if (!companyId || isFolderView) return;
    getFormFoldersRequest({ companyId, userId: user.uuid, limit: FORM_FOLDERS_FETCH_LIMIT })
      .then(({ folders: fetchedFolders }) => {
        if (isMountedRef.current) setFolders(fetchedFolders);
      })
      .catch(() => null);
  }, [companyId, user.uuid, isFolderView]);

  const folderSkeletonCount = useMemo(() => {
    if (!loadingFolderView) return 0;
    return folders.length > 0 ? folders.length : 3;
  }, [loadingFolderView, folders.length]);

  return (
    <Box padding={padding}>
      <MyFormsToolbar
        isFolderView={isFolderView}
        isArchivedView={isArchivedView}
        isArchivedFoldersView={isArchivedView}
        folders={folders}
        onSearchChange={handleSearchChange}
        onFolderSearchChange={value => setFolderSearch(value)}
        onFolderFilterChange={folderId => {
          setFolderFilter(folderId);
          setOffset(0);
        }}
        onArchivedViewChange={checked => {
          setIsArchivedView(checked);
          setOffset(0);
        }}
        onArchivedFoldersViewChange={checked => {
          setIsArchivedView(checked);
          setOffset(0);
        }}
        onFolderViewChange={checked => {
          setOffset(0);
          history.push(checked ? '/forms/my-forms' : '/forms/my-forms?view=list');
        }}
        onCreateFolder={() => setOpenCreateFolderDialog(true)}
        onCreateForm={() => history.push('/forms/new-form')}
      />

      {isFolderView ? (
        <SafeDndProvider backend={HTML5Backend}>
          <Box minHeight="calc(100vh - 280px)">
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
              gridGap={16}
              marginBottom={3}
            >
              {loadingFolderView
                ? Array.from({ length: folderSkeletonCount }, (_, i) => (
                    <FolderCardSkeleton key={`folder-skeleton-${i}`} />
                  ))
                : folders.map(folder => (
                    <FolderCard
                      key={folder.id}
                      folder={folder}
                      dragType={FORM_DRAG_TYPE}
                      onDrop={async (form, folderId) => {
                        const previousUnassigned = [...unassignedForms];
                        const previousFolderForms = [...(folderFormsMap[folderId] || [])];
                        setUnassignedForms(prev => prev.filter(item => item.id !== form.id));
                        setFolderFormsMap(prev => ({
                          ...prev,
                          [folderId]: [...(prev[folderId] || []), form],
                        }));
                        try {
                          await addFormToFolderRequest({
                            companyId,
                            formId: form.id || '',
                            folderId,
                            userId: user.uuid,
                          });
                          await refreshUnassignedAfterMutation();
                          await reloadFoldersBoard();
                        } catch (error) {
                          setUnassignedForms(previousUnassigned);
                          setFolderFormsMap(prev => ({ ...prev, [folderId]: previousFolderForms }));
                          showNotification({
                            message: 'Failed to move form to folder',
                            options: { variant: 'error' },
                          });
                        }
                      }}
                      onDeleteFolder={async folderId => {
                        setSelectedFolderId(folderId);
                        setOpenDeleteFolderDialog(true);
                      }}
                      onMoveFolderToFolder={async (sourceFolderId, targetFolderId) => {
                        setLoadingFolderView(true);
                        try {
                          await moveFormFolderRequest({
                            companyId,
                            folderId: sourceFolderId,
                            newParentId: targetFolderId,
                            userId: user.uuid,
                          });
                          showNotificationRef.current({
                            message: 'Folder moved',
                            options: { variant: 'success' },
                          });
                          await reloadFoldersBoard();
                        } catch (error) {
                          handleServerErrorRef.current(error);
                          setLoadingFolderView(false);
                        }
                      }}
                      onMoveFolder={targetFolder => {
                        setSelectedFolder(targetFolder);
                        setOpenMoveFolderDialog(true);
                      }}
                      onRenameFolder={targetFolder => {
                        setSelectedFolder(targetFolder);
                        setOpenRenameFolderDialog(true);
                      }}
                      onArchiveFolder={async targetFolder => {
                        try {
                          await archiveFormFolderRequest({
                            companyId,
                            folderId: targetFolder.id,
                            userId: user.uuid,
                            archived: targetFolder.archived !== true,
                          });
                          showNotificationRef.current({
                            message: targetFolder.archived !== true
                              ? 'Folder archived'
                              : 'Folder unarchived',
                            options: { variant: 'success' },
                          });
                          await reloadFoldersBoard();
                        } catch (error) {
                          handleServerErrorRef.current(error);
                        }
                      }}
                      onColourFolder={async (folderId, colour) => {
                        const previousColour = folders.find(f => f.id === folderId)?.colour ?? null;
                        setFolders(prev =>
                          prev.map(f => (f.id === folderId ? { ...f, colour } : f))
                        );
                        try {
                          await updateFormFolderRequest({
                            companyId,
                            folderId,
                            userId: user.uuid,
                            colour,
                          });
                        } catch (error) {
                          setFolders(prev =>
                            prev.map(f => (f.id === folderId ? { ...f, colour: previousColour } : f))
                          );
                          handleServerErrorRef.current(error);
                        }
                      }}
                      onOpenFolder={() =>
                        history.push(
                          `/forms/my-forms/folders/${buildFormUrlSegment(folder.name, folder.id)}`
                        )
                      }
                    />
                  ))}
              {!loadingFolderView && folders.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  No Folders
                </Typography>
              )}
            </Box>

            <UnassignedFormsSection
              rows={unassignedForms}
              dragType={FORM_DRAG_TYPE}
              withActions
              onEditForm={form =>
                history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}`)
              }
              onPreviewForm={form =>
                history.push(
                  `/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}?preview=true`
                )
              }
              onDuplicateForm={async form => {
                const newFormId = await duplicateForm(form.id || '');
                if (newFormId)
                  history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', newFormId)}`);
                await refreshUnassignedAfterMutation();
              }}
              onSettingsForm={form =>
                history.push(
                  `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/settings`
                )
              }
              onSendForm={form => {
                setSelectedFormId(form.id);
                setOpenSendFormDialog(true);
              }}
              onArchiveForm={form => {
                setSelectedFormId(form.id);
                setOpenArchiveFormDialog(true);
              }}
              onViewAnswers={form =>
                history.push(
                  `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/summary`
                )
              }
              onDeleteForm={form => {
                setSelectedFormId(form.id);
                setOpenDeleteFormDialog(true);
              }}
              onChangeFolder={form => {
                setSelectedFormId(form.id);
                setOpenChangeFolderDialog(true);
              }}
              onViewOutstanding={form => {
                setSelectedFormId(form.id);
                setDialogType('outstanding');
                setOpenRecipientsDialog(true);
              }}
              onViewParticipants={(form, type) => {
                setSelectedFormId(form.id);
                setDialogType(type);
                setOpenRecipientsDialog(true);
              }}
              onResendAll={form => {
                setSelectedFormId(form.id);
                setOpenResendAllDialog(true);
              }}
              onOpenAIAnalytics={form => {
                setSelectedFormForAIAnalytics(form);
                setOpenAIAnalyticsDialog(true);
              }}
              loading={loadingUnassigned}
              total={unassignedTotal}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onPageChange={handlePageChange}
              setOffset={setOffset}
              onSearchChange={handleSearchChange}
            />
          </Box>
        </SafeDndProvider>
      ) : (
        <FormsDataGrid
          rows={forms}
          enableDragAndDrop={false}
          withActions
          onEditForm={form =>
            history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}`)
          }
          onPreviewForm={form =>
            history.push(
              `/forms/my-forms/${buildFormUrlSegment(form.title || '', form.id || '')}?preview=true`
            )
          }
          onDuplicateForm={async form => {
            const newFormId = await duplicateForm(form.id || '');
            if (newFormId)
              history.push(`/forms/my-forms/${buildFormUrlSegment(form.title || '', newFormId)}`);
            getForms(statusFilter, folderFilter || undefined);
          }}
          onSettingsForm={form =>
            history.push(
              `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/settings`
            )
          }
          onSendForm={form => {
            setSelectedFormId(form.id);
            setOpenSendFormDialog(true);
          }}
          onArchiveForm={form => {
            setSelectedFormId(form.id);
            setOpenArchiveFormDialog(true);
          }}
          onViewAnswers={form =>
            history.push(
              `/forms/form-answers/${buildFormUrlSegment(form.title || '', form.id || '')}/summary`
            )
          }
          onDeleteForm={form => {
            setSelectedFormId(form.id);
            setOpenDeleteFormDialog(true);
          }}
          onChangeFolder={form => {
            setSelectedFormId(form.id);
            setOpenChangeFolderDialog(true);
          }}
          onViewOutstanding={form => {
            setSelectedFormId(form.id);
            setDialogType('outstanding');
            setOpenRecipientsDialog(true);
          }}
          onViewParticipants={(form, type) => {
            setSelectedFormId(form.id);
            setDialogType(type);
            setOpenRecipientsDialog(true);
          }}
          onResendAll={form => {
            setSelectedFormId(form.id);
            setOpenResendAllDialog(true);
          }}
          onOpenAIAnalytics={form => {
            setSelectedFormForAIAnalytics(form);
            setOpenAIAnalyticsDialog(true);
          }}
          height="calc(100vh - 240px)"
          loading={loading}
          total={total}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          pageSize={GRID_PAGE_SIZE}
          rowsPerPageOptions={[GRID_PAGE_SIZE]}
          onPageChange={handlePageChange}
          setOffset={setOffset}
        />
      )}

      <ConfirmDialog
        open={openArchiveFormDialog}
        handleClose={() => setOpenArchiveFormDialog(false)}
        handleConfirm={async () => {
          if (!selectedFormId) return;
          try {
            setLoadingArchiveForm(true);
            await updateFormStatusRequest({
              companyId,
              formId: selectedFormId,
              userId: user.uuid,
              status: 'archived',
            });
            setOpenArchiveFormDialog(false);
            if (isFolderView) {
              await refreshUnassignedAfterMutation();
            } else {
              getForms(statusFilter, folderFilter || undefined);
            }
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingArchiveForm(false);
          }
        }}
        title="Are you sure you want to archive this form?"
        loading={loadingArchiveForm}
        confirmText="Archive"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={openDeleteFormDialog}
        handleClose={() => setOpenDeleteFormDialog(false)}
        handleConfirm={async () => {
          await deleteForm(selectedFormId || '');
          setOpenDeleteFormDialog(false);
          if (isFolderView) {
            await refreshUnassignedAfterMutation();
          } else {
            getForms(statusFilter, folderFilter || undefined);
          }
        }}
        title="Are you sure you want to delete this form?"
        loading={loadingDeleteForm}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmDialog
        open={openDeleteFolderDialog}
        handleClose={() => {
          setOpenDeleteFolderDialog(false);
          setSelectedFolderId(null);
        }}
        handleConfirm={async () => {
          if (!selectedFolderId) return;
          try {
            setDeletingFolder(true);
            await deleteFormFolderRequest({
              companyId,
              folderId: selectedFolderId,
              userId: user.uuid,
              mode: 'cascade',
            });
            setOpenDeleteFolderDialog(false);
            setSelectedFolderId(null);
            await reloadFoldersBoard();
            await refreshUnassignedAfterMutation();
          } finally {
            setDeletingFolder(false);
          }
        }}
        title="Are you sure you want to delete this folder?"
        subtitle="All nested folders inside this folder will also be deleted."
        loading={deletingFolder}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ChangeFolderDialog
        open={openChangeFolderDialog}
        loading={loadingChangeFolder}
        companyId={companyId}
        userId={user.uuid}
        onClose={() => {
          setOpenChangeFolderDialog(false);
          setSelectedFormId(undefined);
        }}
        onConfirm={async selectedTargetFolderId => {
          if (!selectedFormId || !selectedTargetFolderId) return;
          try {
            setLoadingChangeFolder(true);
            await addFormToFolderRequest({
              companyId,
              formId: selectedFormId,
              folderId: selectedTargetFolderId,
              userId: user.uuid,
            });
            showNotificationRef.current({
              message: 'Form moved to another folder',
              options: { variant: 'success' },
            });
            setOpenChangeFolderDialog(false);
            setSelectedFormId(undefined);
            if (isFolderView) {
              await refreshUnassignedAfterMutation();
              await reloadFoldersBoard();
            } else {
              await getForms(statusFilter, folderFilter || undefined);
            }
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setLoadingChangeFolder(false);
          }
        }}
      />

      <FormProvider {...recipientsFormMethods}>
        <form>
          <FormSendDialog
            open={openSendFormDialog}
            onClose={() => setOpenSendFormDialog(false)}
            isPhoneInputDisabled={isPhoneInputDisabled}
            handleEmailFieldKeyDown={handleEmailFieldKeyDown}
            handlePhoneFieldKeyDown={handlePhoneFieldKeyDown}
            addEmailToList={addEmailToList}
            addPhoneToList={addPhoneToList}
            deleteContact={deleteContact}
            emails={emails}
            phones={phones}
            setIsPhoneInputDisabled={setIsPhoneInputDisabled}
            bulkPaste={bulkPaste}
            bulkLimitHint={bulkLimitHint}
            sendFormRecipients={handleSendFormRecipients}
            loading={loadingSendForm}
          />
        </form>
      </FormProvider>

      <FormAIAnalyticsDialog
        open={openAIAnalyticsDialog}
        onClose={() => {
          setOpenAIAnalyticsDialog(false);
          setSelectedFormForAIAnalytics(null);
        }}
        selectedFormForAIAnalytics={selectedFormForAIAnalytics}
        user={user}
        companyId={companyId}
      />

      <ConfirmDialog
        open={openResendAllDialog}
        handleClose={() => setOpenResendAllDialog(false)}
        handleConfirm={handleResendAll}
        loading={loadingResendAll}
        confirmText="Resend"
        cancelText="Cancel"
        title="Are you sure you want to resend all messages to all recipients, who haven't filled the form yet?"
      />
      {selectedFormId && (
        <FormRecipientsDialog
          open={openRecipientsDialog}
          onClose={() => {
            setOpenRecipientsDialog(false);
          }}
          type={dialogType}
          companyId={companyId}
          formId={selectedFormId}
          user={user}
        />
      )}

      <MoveFolderDialog
        open={openMoveFolderDialog}
        loading={movingFolder}
        companyId={companyId}
        userId={user.uuid}
        folderId={selectedFolder?.id ?? null}
        currentParentId={selectedFolder?.parentId ?? null}
        onClose={() => {
          setOpenMoveFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async newParentId => {
          if (!selectedFolder) return;
          try {
            setMovingFolder(true);
            await moveFormFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              newParentId,
              userId: user.uuid,
            });
            showNotificationRef.current({ message: 'Folder moved', options: { variant: 'success' } });
            setOpenMoveFolderDialog(false);
            setSelectedFolder(null);
            await reloadFoldersBoard();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setMovingFolder(false);
          }
        }}
      />

      <RenameFolderDialog
        open={openRenameFolderDialog}
        initialName={selectedFolder?.name ?? ''}
        loading={renamingFolder}
        onClose={() => {
          setOpenRenameFolderDialog(false);
          setSelectedFolder(null);
        }}
        onConfirm={async name => {
          if (!selectedFolder) return;
          try {
            setRenamingFolder(true);
            await updateFormFolderRequest({
              companyId,
              folderId: selectedFolder.id,
              userId: user.uuid,
              name,
            });
            showNotificationRef.current({ message: 'Folder renamed', options: { variant: 'success' } });
            setOpenRenameFolderDialog(false);
            setSelectedFolder(null);
            await reloadFoldersBoard();
          } catch (error) {
            handleServerErrorRef.current(error);
          } finally {
            setRenamingFolder(false);
          }
        }}
      />

      <CreateFolderDialog
        open={openCreateFolderDialog}
        folderName={newFolderName}
        loading={creatingFolder}
        onFolderNameChange={setNewFolderName}
        onClose={() => setOpenCreateFolderDialog(false)}
        onCreate={async colour => {
          if (!newFolderName.trim()) return;
          try {
            setCreatingFolder(true);
            await createFormFolderRequest({
              companyId,
              userId: user.uuid,
              name: newFolderName.trim(),
              colour,
            });
            setNewFolderName('');
            setOpenCreateFolderDialog(false);
            await reloadFoldersBoard();
            if (isFolderView) {
              await refreshUnassignedAfterMutation();
            } else {
              await getForms(statusFilter, folderFilter || undefined);
            }
          } finally {
            setCreatingFolder(false);
          }
        }}
      />
    </Box>
  );
};
